/**
 * React Query hooks for Technician Access API (COMP-003).
 * IP-003: GET /api/technicians/me, GET /api/technicians/me/eligibility
 * Admin: GET /api/admin/technicians, GET /api/admin/technicians/:userId/eligibility
 * Admin write: search Entra users, update attributes, revoke access
 *
 * Graph API eventual consistency: extension attribute writes may take seconds to
 * appear in filtered /users queries. We maintain a local overlay of recent mutations
 * and merge it into fetched data so the UI always shows the latest known values.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/providers/api-provider";
import type {
  TechnicianSummary,
  TechnicianEligibilityResponse,
  EligibilitySummary,
  EntraUserSearchResult,
  TechnicianAttributeUpdate,
  PaginatedResponse,
} from "@/types";

// ── Overlay for Graph API eventual consistency ──────────────────────────

interface TechnicianOverlay {
  updates: Map<string, TechnicianSummary>;
  removals: Set<string>;
}

const OVERLAY_KEY = ["technicians", "__overlay__"];

function getOverlay(
  queryClient: ReturnType<typeof useQueryClient>,
): TechnicianOverlay {
  return (
    queryClient.getQueryData<TechnicianOverlay>(OVERLAY_KEY) ?? {
      updates: new Map(),
      removals: new Set(),
    }
  );
}

function setOverlay(
  queryClient: ReturnType<typeof useQueryClient>,
  overlay: TechnicianOverlay,
) {
  queryClient.setQueryData(OVERLAY_KEY, overlay);
}

/**
 * Apply the overlay to fetched list data: merge updated technicians,
 * filter out removed technicians.
 */
function applyOverlay(
  data: PaginatedResponse<TechnicianSummary>,
  overlay: TechnicianOverlay,
): PaginatedResponse<TechnicianSummary> {
  let items = data.items.map((t) => overlay.updates.get(t.userId) ?? t);
  const removedCount = items.filter((t) =>
    overlay.removals.has(t.userId),
  ).length;
  items = items.filter((t) => !overlay.removals.has(t.userId));
  return {
    ...data,
    items,
    total: data.total - removedCount,
  };
}

// ── Query hooks ─────────────────────────────────────────────────────────

interface TechnicianListParams {
  page?: number;
  pageSize?: number;
}

export function useTechnicianList(params: TechnicianListParams = {}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { page = 1, pageSize = 25 } = params;

  return useQuery({
    queryKey: ["technicians", { page, pageSize }],
    queryFn: () =>
      api.get<PaginatedResponse<TechnicianSummary>>(
        `/api/admin/technicians?page=${page}&pageSize=${pageSize}`,
      ),
    staleTime: 60_000,
    select: (data) => applyOverlay(data, getOverlay(queryClient)),
  });
}

export function useTechnicianDetail(userId: string | undefined) {
  const api = useApiClient();

  return useQuery({
    queryKey: ["technicians", userId, "eligibility"],
    queryFn: () =>
      api.get<TechnicianEligibilityResponse>(
        `/api/admin/technicians/${userId}/eligibility`,
      ),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useTechnicianMe() {
  const api = useApiClient();

  return useQuery({
    queryKey: ["technicians", "me"],
    queryFn: () => api.get<TechnicianSummary>("/api/technicians/me"),
    staleTime: 60_000,
  });
}

export function useEligibilityCheck(customerId: string | undefined) {
  const api = useApiClient();

  return useQuery({
    queryKey: ["technicians", "me", "eligibility", customerId],
    queryFn: () =>
      api.get<TechnicianEligibilityResponse>(
        `/api/technicians/me/eligibility?customerId=${customerId}`,
      ),
    enabled: !!customerId,
    staleTime: 0, // Always fresh per spec
  });
}

export function useEligibilitySummary() {
  const api = useApiClient();

  return useQuery({
    queryKey: ["technicians", "eligibility-summary"],
    queryFn: () =>
      api.get<EligibilitySummary>("/api/admin/stats/eligibility-summary"),
    staleTime: 60_000,
  });
}

export function useEntraUserSearch(query: string) {
  const api = useApiClient();

  return useQuery({
    queryKey: ["entra-users", "search", query],
    queryFn: () =>
      api.get<EntraUserSearchResult[]>(
        `/api/admin/entra-users/search?q=${encodeURIComponent(query)}`,
      ),
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
}

// ── Mutation hooks ──────────────────────────────────────────────────────

export function useUpdateTechnicianAttributes() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      attributes,
    }: {
      userId: string;
      attributes: TechnicianAttributeUpdate;
    }) =>
      api.patch<TechnicianSummary>(
        `/api/admin/technicians/${userId}/attributes`,
        attributes,
      ),
    onSuccess: (updatedTechnician) => {
      // Store in overlay so all current and future list queries show it
      const overlay = getOverlay(queryClient);
      overlay.updates.set(updatedTechnician.userId, updatedTechnician);
      setOverlay(queryClient, overlay);
      // Trigger re-render of active list queries (select will apply overlay)
      void queryClient.invalidateQueries({
        queryKey: ["technicians"],
        refetchType: "none",
      });
    },
  });
}

export function useRevokeTechnicianAccess() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      api.del<{ success: boolean }>(`/api/admin/technicians/${userId}`),
    onSuccess: (_data, userId) => {
      // Store in overlay so all current and future list queries exclude it
      const overlay = getOverlay(queryClient);
      overlay.removals.add(userId);
      overlay.updates.delete(userId);
      setOverlay(queryClient, overlay);
      // Trigger re-render of active list queries (select will apply overlay)
      void queryClient.invalidateQueries({
        queryKey: ["technicians"],
        refetchType: "none",
      });
    },
  });
}
